import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar, RadialBarChart, RadialBar,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PolarAngleAxis,
} from 'recharts';
import {
  Activity, Bell, ShieldAlert, Star, Radio, Database, ScrollText, Gauge,
} from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';
import { CONSOLE, GRID_INK, AXIS_INK, SEVERITY_RAMP, REACHABLE_HEX, UNREACHABLE_HEX, SERIES_SINGLE, SERIES_OPENED } from '../theme/console';
import TenantSelect from '../components/TenantSelect';

interface DayCount { day: string; count: number }
interface NamedCount { severity?: string; status?: string; tactic?: string; host?: string; label?: string | null; count: number }

interface Stats {
  logsCount: number;
  alertsCount: number;
  casesCount: number;
  casesBySeverity: NamedCount[];
  casesByStatus: NamedCount[];
  eventsTimeseries: DayCount[];
  alertsTimeseries: DayCount[];
  mitreTactics: NamedCount[];
  topHosts: NamedCount[];
  assets: { total: number; businessCritical: number; unreachable: number };
  brokers: { total: number; online: number };
  openDataQualityIssues: number;
}

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low'];
const SEVERITY_HEX = SEVERITY_RAMP;
const STATUS_ORDER = ['new', 'investigating', 'contained', 'closed'];
// Stati del case: una sola misura per categoria, l'identità la danno le etichette dell'asse —
// il colore non deve distinguere nulla, quindi tinta unica invece di quattro tinte che
// rischierebbero di non separarsi fra loro.
const STATUS_HEX: Record<string, string> = { new: SERIES_SINGLE, investigating: SERIES_SINGLE, contained: SERIES_SINGLE, closed: SERIES_SINGLE };
const STATUS_LABEL: Record<string, string> = { new: 'New', investigating: 'Investigating', contained: 'Contained', closed: 'Closed' };

function formatDay(day: string): string {
  const d = new Date(day);
  return Number.isNaN(d.getTime()) ? day : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

// Combina serie eventi/alert per giorno in un'unica struttura dati per il grafico a due aree.
function mergeTimeseries(events: DayCount[], alerts: DayCount[]): { day: string; events: number; alerts: number }[] {
  const days = [...new Set([...events.map(e => e.day), ...alerts.map(a => a.day)])].sort();
  const eventsByDay = Object.fromEntries(events.map(e => [e.day, e.count]));
  const alertsByDay = Object.fromEntries(alerts.map(a => [a.day, a.count]));
  return days.map(day => ({ day, events: eventsByDay[day] || 0, alerts: alertsByDay[day] || 0 }));
}

export default function OverviewPage() {
  const { selectedTenantId } = useTenantScope();
  const [stats, setStats] = useState<Stats | null>(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    axios.get('/api/stats', { params: { tenantId: selectedTenantId ?? undefined } })
      .then(res => { setStats(res.data.data); setNow(new Date()); });
  }, [selectedTenantId]);

  const severityCounts = Object.fromEntries((stats?.casesBySeverity || []).map(s => [s.severity, s.count]));
  const statusCounts = Object.fromEntries((stats?.casesByStatus || []).map(s => [s.status, s.count]));
  const trend = mergeTimeseries(stats?.eventsTimeseries || [], stats?.alertsTimeseries || []);

  const severityData = SEVERITY_ORDER.map(sev => ({ name: sev, value: severityCounts[sev] || 0 }))
    .filter(d => d.value > 0);
  const statusData = STATUS_ORDER.map(st => ({ name: STATUS_LABEL[st], key: st, value: statusCounts[st] || 0 }));
  const mitreData = (stats?.mitreTactics || []).map(m => ({ name: m.tactic, value: m.count }));
  const hostsData = (stats?.topHosts || []).map(h => ({ name: h.label ? `${h.label} · ${h.host}` : h.host, value: h.count }));

  // Punteggio composito 0-100: penalizza incidenti critici/high, broker offline, asset business
  // critical irraggiungibili e problemi di data quality non risolti. Puramente indicativo/UI.
  const openCritical = (severityCounts['critical'] || 0) + (severityCounts['high'] || 0);
  const brokerRatio = stats && stats.brokers.total > 0 ? stats.brokers.online / stats.brokers.total : 1;
  const healthScore = stats ? Math.max(0, Math.round(
    100
    - Math.min(40, openCritical * 6)
    - Math.min(25, (1 - brokerRatio) * 25)
    - Math.min(20, stats.assets.unreachable * 8)
    - Math.min(15, stats.openDataQualityIssues * 3)
  )) : null;
  const healthColor = healthScore === null ? AXIS_INK : healthScore >= 80 ? '#0ca30c' : healthScore >= 50 ? '#fab219' : '#d03b3b';

  return (
    <div className="space-y-5">
      {/* Hero banner: sfondo sfumato con forme sfocate decorative, KPI "glass" in evidenza. */}
      <div className="relative overflow-hidden rounded-2xl border border-[#23314d] bg-gradient-to-br from-[#16233c] via-[#151f33] to-[#101a2e] px-6 py-7 text-[#e8edf7]">
        <div className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full bg-fuchsia-400/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-indigo-400/30 blur-3xl" />
        <div className="pointer-events-none absolute top-1/2 left-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#151f33]/10 blur-2xl" />

        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-indigo-100">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              LIVE · Updated {now.toLocaleTimeString('en-GB')}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold">Security Operations Overview</h1>
              <TenantSelect variant="console" />
            </div>
            <p className="mt-1 text-sm text-indigo-100/80">Real-time visibility across detections, assets and infrastructure health.</p>
          </div>

          {/* SOC Health Score: gauge circolare, indicatore sintetico dello stato generale. */}
          <div className="flex items-center gap-3 rounded-xl bg-[#151f33]/10 px-4 py-3 backdrop-blur-sm">
            <div className="relative h-16 w-16">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ value: healthScore ?? 0, fill: healthColor }]}
                  startAngle={90} endAngle={-270}>
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar dataKey="value" background={{ fill: 'rgba(255,255,255,0.15)' }} cornerRadius={8} isAnimationActive={false} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center text-sm font-bold">{healthScore ?? '—'}</div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-indigo-100">
                <Gauge size={13} /> SOC Health
              </div>
              <div className="text-xs text-indigo-100/70">Composite score</div>
            </div>
          </div>
        </div>

        <div className="relative mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {[
            // Ogni riquadro apre la pagina da cui viene il numero (26/10/2026).
            { label: 'Events (14d)', value: trend.reduce((s, d) => s + d.events, 0), icon: ScrollText, to: '/logs' },
            { label: 'Active Alerts', value: stats?.alertsCount, icon: Bell, to: '/alerts' },
            { label: 'Open Incidents', value: stats ? STATUS_ORDER.slice(0, 3).reduce((s, st) => s + (statusCounts[st] || 0), 0) : undefined, icon: ShieldAlert, to: '/incidents' },
            { label: 'Business Critical', value: stats?.assets.businessCritical, icon: Star, alert: (stats?.assets.unreachable ?? 0) > 0, to: '/asset-center' },
            { label: 'Brokers Online', value: stats ? `${stats.brokers.online}/${stats.brokers.total}` : undefined, icon: Radio, to: '/sites-brokers' },
            { label: 'Data Quality Issues', value: stats?.openDataQualityIssues, icon: Database, alert: (stats?.openDataQualityIssues ?? 0) > 0, to: '/data-quality' },
          ].map(kpi => (
            <Link key={kpi.label} to={kpi.to} title={`Open ${kpi.label.replace(' (14d)', '')}`}
              className={`block rounded-xl px-3 py-3 backdrop-blur-sm transition hover:ring-1 hover:ring-indigo-200/60 hover:bg-white/10 ${kpi.alert ? 'bg-red-500/20 ring-1 ring-red-300/40' : 'bg-[#151f33]/10'}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-indigo-100/80">
                <kpi.icon size={12} /> {kpi.label}
              </div>
              <div className="mt-1 text-xl font-semibold">{kpi.value ?? '—'}</div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-[#23314d] bg-[#151f33] p-4">
          <h2 className="mb-2 font-medium text-[#e8edf7]">Incidents by severity</h2>
          {severityData.length === 0 ? (
            <p className="py-10 text-center text-sm text-[#6b7f9e]">No incident recorded</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={severityData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3} isAnimationActive={false}>
                  {severityData.map(d => <Cell key={d.name} fill={SEVERITY_HEX[d.name]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: `1px solid ${GRID_INK}`, background: CONSOLE.surfaceRaised, color: CONSOLE.inkPrimary }} />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v: string) => v.charAt(0).toUpperCase() + v.slice(1)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-lg border border-[#23314d] bg-[#151f33] p-4">
          <h2 className="mb-2 font-medium text-[#e8edf7]">Incidents by status</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={statusData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={GRID_INK} />
              <XAxis type="number" tick={{ fontSize: 11, fill: AXIS_INK }} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: CONSOLE.inkSecondary }} axisLine={false} tickLine={false} width={90} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: `1px solid ${GRID_INK}`, background: CONSOLE.surfaceRaised, color: CONSOLE.inkPrimary }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} isAnimationActive={false}>
                {statusData.map(d => <Cell key={d.key} fill={STATUS_HEX[d.key]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-[#23314d] bg-[#151f33] p-4">
          <h2 className="mb-2 font-medium text-[#e8edf7]">MITRE ATT&CK tactics</h2>
          {mitreData.length === 0 ? (
            <p className="py-10 text-center text-sm text-[#6b7f9e]">No tactic mapped yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={mitreData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={GRID_INK} />
                <XAxis type="number" tick={{ fontSize: 11, fill: AXIS_INK }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: CONSOLE.inkSecondary }} axisLine={false} tickLine={false} width={100} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: `1px solid ${GRID_INK}`, background: CONSOLE.surfaceRaised, color: CONSOLE.inkPrimary }} />
                <Bar dataKey="value" fill={SERIES_SINGLE} radius={[0, 6, 6, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-[#23314d] bg-[#151f33] p-4">
          <h2 className="mb-2 font-medium text-[#e8edf7]">Top targeted hosts</h2>
          {hostsData.length === 0 ? (
            <p className="py-10 text-center text-sm text-[#6b7f9e]">No alert recorded</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={hostsData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={GRID_INK} />
                <XAxis type="number" tick={{ fontSize: 11, fill: AXIS_INK }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: CONSOLE.inkSecondary, fontFamily: 'monospace' }} axisLine={false} tickLine={false} width={110} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: `1px solid ${GRID_INK}`, background: CONSOLE.surfaceRaised, color: CONSOLE.inkPrimary }} />
                <Bar dataKey="value" fill={SERIES_SINGLE} radius={[0, 6, 6, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-lg border border-[#23314d] bg-[#151f33] p-4">
          <h2 className="mb-2 font-medium text-[#e8edf7]">Asset & infrastructure health</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Reachable', value: Math.max(0, (stats?.assets.total ?? 0) - (stats?.assets.unreachable ?? 0)) },
                      { name: 'Unreachable', value: stats?.assets.unreachable ?? 0 },
                    ]}
                    dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={3} isAnimationActive={false}>
                    <Cell fill={REACHABLE_HEX} />
                    <Cell fill={UNREACHABLE_HEX} />
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: `1px solid ${GRID_INK}`, background: CONSOLE.surfaceRaised, color: CONSOLE.inkPrimary }} />
                </PieChart>
              </ResponsiveContainer>
              <p className="text-center text-xs text-[#6b7f9e]">Assets reachability ({stats?.assets.total ?? 0} total)</p>
            </div>
            <div className="flex flex-col justify-center gap-3">
              <div className="flex items-center justify-between rounded-lg bg-[#1c2942] px-3 py-2">
                <span className="flex items-center gap-1.5 text-xs text-[#9fb0cc]"><Star size={12} /> Business Critical</span>
                <span className="text-sm font-semibold text-[#e8edf7]">{stats?.assets.businessCritical ?? '—'}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-[#1c2942] px-3 py-2">
                <span className="flex items-center gap-1.5 text-xs text-[#9fb0cc]"><Radio size={12} /> Brokers online</span>
                <span className="text-sm font-semibold text-[#e8edf7]">{stats ? `${stats.brokers.online}/${stats.brokers.total}` : '—'}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-[#1c2942] px-3 py-2">
                <span className="flex items-center gap-1.5 text-xs text-[#9fb0cc]"><Database size={12} /> Data quality issues</span>
                <span className={`text-sm font-semibold ${(stats?.openDataQualityIssues ?? 0) > 0 ? 'text-red-600' : 'text-[#e8edf7]'}`}>
                  {stats?.openDataQualityIssues ?? '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Trend eventi/alert: area chart a doppia serie con riempimento sfumato. In fondo alla
          pagina (26/10/2026): subito sotto i KPI risultava poco chiaro. */}
      <div className="rounded-lg border border-[#23314d] bg-[#151f33] p-4">
        <div className="mb-2 flex items-center gap-2">
          <Activity size={16} className="text-sky-400" />
          <h2 className="font-medium text-[#e8edf7]">Events & Alerts trend — last 14 days</h2>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gradEvents" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={SERIES_OPENED} stopOpacity={0.35} />
                <stop offset="95%" stopColor={SERIES_OPENED} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradAlerts" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={UNREACHABLE_HEX} stopOpacity={0.4} />
                <stop offset="95%" stopColor={UNREACHABLE_HEX} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={GRID_INK} />
            <XAxis dataKey="day" tickFormatter={formatDay} tick={{ fontSize: 11, fill: AXIS_INK }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: AXIS_INK }} axisLine={false} tickLine={false} width={56} />
            <Tooltip labelFormatter={(v) => formatDay(String(v))} contentStyle={{ borderRadius: 8, fontSize: 12, border: `1px solid ${GRID_INK}`, background: CONSOLE.surfaceRaised, color: CONSOLE.inkPrimary }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="events" name="Events" stroke={SERIES_OPENED} fill="url(#gradEvents)" strokeWidth={2} isAnimationActive={false} />
            <Area type="monotone" dataKey="alerts" name="Alerts" stroke={UNREACHABLE_HEX} fill="url(#gradAlerts)" strokeWidth={2} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
